import React from 'react';
import { renderCvHighlighter } from '../utils/cvHighlighter';

function gruppiereKenntnisse(kenntnisse = []) {
  const kategorisierte = kenntnisse.some(k => (k.kategorie || '').trim());
  if (!kategorisierte) return null;

  const gruppen = [];
  const indexByKategorie = new Map();
  kenntnisse.forEach((kenntnis) => {
    const titel = (kenntnis.titel || '').trim();
    if (!titel) return;
    const kategorie = (kenntnis.kategorie || '').trim() || 'Weitere Kenntnisse';
    if (!indexByKategorie.has(kategorie)) {
      indexByKategorie.set(kategorie, gruppen.length);
      gruppen.push({ kategorie, titel: [] });
    }
    gruppen[indexByKategorie.get(kategorie)].titel.push(titel);
  });
  return gruppen;
}

const kurzUrl = url => (url || '').replace(/^https?:\/\/(www\.)?/, '');

export function Kopf({ profil, nutzer, fotoUrl, onFotoClick, markierungen = [] }) {
  const vollName = `${nutzer?.vorname || ''} ${nutzer?.nachname || ''}`.trim() || nutzer?.name || '';
  const adresse = nutzer?.anschrift || [nutzer?.strasse, nutzer?.hausnummer, nutzer?.plz, nutzer?.ort].filter(Boolean).join(' ');
  const kontakt = [adresse, nutzer?.telefon, nutzer?.email].filter(Boolean).join(' • ');
  const links = [
    nutzer?.linkedin  ? `LinkedIn: ${kurzUrl(nutzer.linkedin)}`  : null,
    nutzer?.portfolio ? `Website: ${kurzUrl(nutzer.portfolio)}`  : null,
    nutzer?.github    ? `GitHub: ${kurzUrl(nutzer.github)}`      : null,
  ].filter(Boolean).join(' • ');
  return (
    <div className={`cv-s-kopf${fotoUrl ? ' cv-s-kopf--mit-foto' : ''}`}>
      <div className="cv-kopf-oben">
        <div className="cv-kopf-text cv-s-kopf-text">
          <h1 className="cv-s-name">{vollName}</h1>
          <p className="cv-s-kontakt">{kontakt}</p>
          {links && <p className="cv-s-kontakt">{links}</p>}
        </div>
      </div>
    </div>
  );
}

export function Sektion({ s, sektionIdx, zeigeTitel = true, vonEintrag = 0, bisEintrag, markierungen = [], fotoUrl = null, skeleton = false }) {
  const eintraege = s.typ === 'eintraege' ? s.eintraege.slice(vonEintrag, bisEintrag) : [];
  const mitFoto = !!(fotoUrl && zeigeTitel);

  return (
    <div className={`cv-s-sektion${mitFoto ? ' cv-s-sektion--mit-foto' : ''}`}>
      {mitFoto && <img src={fotoUrl} className="cv-s-foto-overlay" alt="" />}
      {zeigeTitel && (
        <h2
          className="cv-s-sektion-titel"
          {...(sektionIdx !== undefined ? { 'data-sektion-titel': sektionIdx } : {})}
        >
          {s.name.toUpperCase()}
        </h2>
      )}

      {s.typ === 'eintraege' && (
        <div className="cv-s-eintraege">
          {eintraege.map((e, localIdx) => {
            const globalIdx = vonEintrag + localIdx;
            const job = e.jobtitel || e.rolle || '';
            const org = e.arbeitgeber || e.firma || '';
            const titelText = job && org ? `${job}, ${org}` : (job || org);
            // Firma und Ort stecken als "Firma · Ort" im org-Feld — für die Einzel-Kapseln aufteilen.
            const sepIdx = org.indexOf(' · ');
            const firma = sepIdx >= 0 ? org.slice(0, sepIdx) : org;
            const ort = sepIdx >= 0 ? org.slice(sepIdx + 3) : '';
            // Erst wenn Job, Firma UND Ort da sind, gilt der Titel als "geformt" → normale Zeile statt Kapseln.
            // So bleibt die Ort-Kapsel sichtbar, bis der Ort tatsächlich eingetragen wurde.
            const titelGeformt = job && firma && ort;
            return (
              <div
                key={globalIdx}
                className="cv-s-eintrag"
                {...(sektionIdx !== undefined ? { 'data-eintrag': `${sektionIdx}-${globalIdx}` } : {})}
              >
                <div className="cv-s-eintrag-datum">
                  {e.datum ? e.datum : (skeleton && <span className="cv-skel cv-skel-datum" />)}
                </div>
                <div className="cv-s-eintrag-inhalt">
                  {(!skeleton || titelGeformt) ? (
                    titelText && (
                      <div className="cv-s-eintrag-titel">
                        {renderCvHighlighter(titelText, markierungen)}
                      </div>
                    )
                  ) : (
                    <div className="cv-s-eintrag-titel cv-skel-titel-row">
                      {job ? <span>{renderCvHighlighter(job, markierungen)}</span>
                           : <span className="cv-skel cv-skel-titel-teil" />}
                      <span className="cv-skel-sep cv-skel-comma">,</span>
                      {firma ? <span>{renderCvHighlighter(firma, markierungen)}</span>
                             : <span className="cv-skel cv-skel-titel-teil" />}
                      <span className="cv-skel-sep">·</span>
                      {ort ? <span>{renderCvHighlighter(ort, markierungen)}</span>
                           : <span className="cv-skel cv-skel-titel-teil" />}
                    </div>
                  )}
                  {skeleton ? (
                    // Bearbeiten-Modus: leere Stichpunkte des gerade offenen Eintrags als Platzhalter
                    // zeigen. Verlassene Einträge haben dank Aufräumen keine leeren Stichpunkte mehr.
                    (e.bullets?.length > 0) ? (
                      <ul className="cv-s-bullets">
                        {e.bullets.map((b, j) => (
                          <li key={j}>
                            {(b || '').trim()
                              ? renderCvHighlighter(b, markierungen)
                              : <span className="cv-skel cv-skel-bullet" />}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <ul className="cv-s-bullets">
                        <li><span className="cv-skel cv-skel-bullet" /></li>
                        <li><span className="cv-skel cv-skel-bullet cv-skel-bullet-short" /></li>
                      </ul>
                    )
                  ) : (
                    // Fertiger Lebenslauf: nur echte Stichpunkte, keine leeren Punkte.
                    (e.bullets || []).some(b => (b || '').trim()) && (
                      <ul className="cv-s-bullets">
                        {e.bullets.filter(b => (b || '').trim()).map((b, j) => (
                          <li key={j}>{renderCvHighlighter(b, markierungen)}</li>
                        ))}
                      </ul>
                    )
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {s.typ === 'kenntnisse' && (() => {
        const gruppen = gruppiereKenntnisse(s.kenntnisse || []);
        if (gruppen) {
          return (
            <div className="cv-s-skills-grouped">
              {gruppen.map((gruppe) => (
                <div className="cv-s-skill-row" key={gruppe.kategorie}>
                  <div className="cv-s-skill-category">{gruppe.kategorie}</div>
                  <div className="cv-s-skill-list">{gruppe.titel.join(' • ')}</div>
                </div>
              ))}
            </div>
          );
        }
        return (
          <p className="cv-s-skills">
            {(s.kenntnisse || []).map(k => k.titel).filter(Boolean).join(' • ')}
          </p>
        );
      })()}

      {s.typ === 'bullets' && (
        <ul className="cv-s-bullets cv-s-bullets-sektion">
          {(s.bullets || []).map((b, i) => (
            <li key={i}>{renderCvHighlighter(b, markierungen)}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function Footer({ nutzer }) {
  const vollName = `${nutzer?.vorname || ''} ${nutzer?.nachname || ''}`.trim() || nutzer?.name || '';
  const adresse = nutzer?.anschrift || [nutzer?.strasse, nutzer?.hausnummer, nutzer?.plz, nutzer?.ort].filter(Boolean).join(' ');
  const kontakt = [vollName, adresse, nutzer?.telefon, nutzer?.email, nutzer?.website, nutzer?.linkedin, nutzer?.portfolio, nutzer?.github]
    .filter(Boolean)
    .join(' • ');
  return <div className="cv-s-footer">{kontakt}</div>;
}
